import { useState, useEffect, useCallback } from 'react';
import { leaveAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { CalendarDays, Plus, CheckCircle2, XCircle, Clock, FileText, Briefcase, AlertTriangle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';

const LeavePage = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_super_admin || ['hospital_admin', 'hr'].includes(user?.role_code) || ['Hospital Admin', 'HR'].includes(user?.role_name);

  const [applications, setApplications] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [activeTab, setActiveTab] = useState(isAdmin ? 'pending' : 'my_leaves');
  // v36 — Approve / Reject decision dialog with mandatory comment.
  const [decisionDialog, setDecisionDialog] = useState(null);  // { app, action: 'approve'|'reject' }
  const [decisionComment, setDecisionComment] = useState('');
  const [decisionSubmitting, setDecisionSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [appsRes, typesRes, balRes] = await Promise.all([
        leaveAPI.getApplications().catch(() => ({ data: [] })),
        leaveAPI.getTypes().catch(() => ({ data: [] })),
        leaveAPI.getBalance().catch(() => ({ data: [] })),
      ]);
      setApplications(appsRes.data || []);
      setLeaveTypes(typesRes.data || []);
      setBalances(balRes.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openDecision = (app, action) => {
    setDecisionDialog({ app, action });
    setDecisionComment('');
  };
  const closeDecision = () => {
    setDecisionDialog(null);
    setDecisionComment('');
  };
  const submitDecision = async () => {
    if (!decisionDialog) return;
    const trimmed = decisionComment.trim();
    if (trimmed.length < 5) {
      toast.error('Please add a comment (min 5 chars) explaining your decision.');
      return;
    }
    setDecisionSubmitting(true);
    try {
      const { app, action } = decisionDialog;
      const payload = action === 'approve'
        ? { status: 'approved', approval_comment: trimmed }
        : { status: 'rejected', rejection_reason: trimmed, approval_comment: trimmed };
      await leaveAPI.approve(app.id, payload);
      toast.success(`Leave ${action === 'approve' ? 'approved' : 'rejected'}`);
      closeDecision();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${decisionDialog.action}`);
    } finally {
      setDecisionSubmitting(false);
    }
  };

  const pendingApps = applications.filter(a => a.status === 'pending');
  const myApps = applications.filter(a => a.user_id === user?.id);
  const approvedApps = applications.filter(a => a.status === 'approved');
  const rejectedApps = applications.filter(a => a.status === 'rejected');

  const tabs = isAdmin
    ? [
        { key: 'pending', label: 'Pending Approvals', count: pendingApps.length, icon: Clock },
        { key: 'all', label: 'All Applications', count: applications.length, icon: FileText },
        { key: 'approved', label: 'Approved', count: approvedApps.length, icon: CheckCircle2 },
        { key: 'rejected', label: 'Rejected', count: rejectedApps.length, icon: XCircle },
      ]
    : [
        { key: 'my_leaves', label: 'My Leave Balance', count: null, icon: Briefcase },
        { key: 'my_applications', label: 'My Applications', count: myApps.length, icon: FileText },
      ];

  const getFilteredApps = () => {
    switch (activeTab) {
      case 'pending': return pendingApps;
      case 'approved': return approvedApps;
      case 'rejected': return rejectedApps;
      case 'my_applications': return myApps;
      case 'all': return applications;
      default: return [];
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="leave-page-loading">
        <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Leave Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">{isAdmin ? 'Manage staff leave applications' : 'Track your leave balance & applications'}</p>
        </div>
        <Button onClick={() => setShowApply(true)} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="apply-leave-btn">
          <Plus size={16} /> Apply for Leave
        </Button>
      </div>

      {/* KPI / Summary Cards */}
      {isAdmin ? (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center"><Clock size={18} className="text-amber-600" /></div>
            <div><p className="text-[10px] text-slate-500 uppercase font-medium">Pending</p><p className="text-lg font-bold text-amber-600">{pendingApps.length}</p></div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle2 size={18} className="text-emerald-600" /></div>
            <div><p className="text-[10px] text-slate-500 uppercase font-medium">Approved</p><p className="text-lg font-bold text-emerald-600">{approvedApps.length}</p></div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center"><XCircle size={18} className="text-red-600" /></div>
            <div><p className="text-[10px] text-slate-500 uppercase font-medium">Rejected</p><p className="text-lg font-bold text-red-600">{rejectedApps.length}</p></div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><FileText size={18} className="text-blue-600" /></div>
            <div><p className="text-[10px] text-slate-500 uppercase font-medium">Total</p><p className="text-lg font-bold text-blue-600">{applications.length}</p></div>
          </div>
        </div>
      ) : (
        /* Leave Balance Cards for regular users */
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {balances.length > 0 ? balances.map((b, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`balance-card-${i}`}>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{b.leave_type?.name || b.leave_type_id}</p>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{b.balance ?? b.remaining ?? 0}</p>
                  <p className="text-[10px] text-slate-400">remaining</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Total: {b.total ?? b.allocated ?? 0}</p>
                  <p className="text-xs text-slate-500">Used: {b.used ?? 0}</p>
                </div>
              </div>
              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, ((b.used ?? 0) / (b.total ?? b.allocated ?? 1)) * 100)}%` }} />
              </div>
            </div>
          )) : (
            <div className="col-span-4 text-center py-8 text-slate-400 text-sm">No leave balances configured</div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {tabs.map(tab => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px',
              activeTab === tab.key
                ? 'border-teal-600 text-teal-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )} data-testid={`tab-${tab.key}`}>
            <tab.icon size={14} />
            {tab.label}
            {tab.count !== null && (
              <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full ml-1',
                activeTab === tab.key ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'
              )}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'my_leaves' ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Leave Type</th>
                <th className="text-center px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Total</th>
                <th className="text-center px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Used</th>
                <th className="text-center px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Remaining</th>
                <th className="text-center px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Progress</th>
              </tr>
            </thead>
            <tbody>
              {balances.length > 0 ? balances.map((b, i) => {
                const total = b.total ?? b.allocated ?? 0;
                const used = b.used ?? 0;
                const remaining = b.balance ?? b.remaining ?? (total - used);
                return (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{b.leave_type?.name || b.leave_type_id}</td>
                    <td className="px-4 py-3 text-center text-slate-600">{total}</td>
                    <td className="px-4 py-3 text-center text-slate-600">{used}</td>
                    <td className="px-4 py-3 text-center font-bold text-teal-700">{remaining}</td>
                    <td className="px-4 py-3">
                      <div className="w-full max-w-32 mx-auto h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full" style={{ width: `${Math.min(100, (used / (total || 1)) * 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400">No leave balances configured</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Applications Table */
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b">
                {isAdmin && <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Staff</th>}
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Leave Type</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">From</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">To</th>
                <th className="text-center px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Days</th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Reason</th>
                <th className="text-center px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Status</th>
                {isAdmin && activeTab === 'pending' && <th className="text-center px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {getFilteredApps().length === 0 ? (
                <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-10 text-slate-400">
                  {activeTab === 'pending' ? 'No pending approvals' : 'No applications found'}
                </td></tr>
              ) : getFilteredApps().map(app => (
                <tr key={app.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`leave-app-${app.id}`}>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-slate-800">{app.user ? `${app.user.first_name || ''} ${app.user.last_name || ''}`.trim() : '-'}</p>
                      <p className="text-[10px] text-slate-400">{app.user?.email || ''}</p>
                    </td>
                  )}
                  <td className="px-4 py-3 text-xs text-slate-700 font-medium">{app.leave_type?.name || app.leave_type_id || '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{app.from_date || app.start_date}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{app.to_date || app.end_date}</td>
                  <td className="px-4 py-3 text-center text-xs font-bold text-slate-700">{app.days || 1}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-40 truncate">{app.reason || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('text-[10px] px-2.5 py-1 rounded-full font-bold uppercase',
                      app.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    )}>{app.status}</span>
                  </td>
                  {isAdmin && activeTab === 'pending' && (
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button size="sm" variant="outline"
                          className="h-7 text-[11px] gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => openDecision(app, 'approve')} data-testid={`approve-${app.id}`}>
                          <CheckCircle2 size={12} /> Approve
                        </Button>
                        <Button size="sm" variant="outline"
                          className="h-7 text-[11px] gap-1 border-red-200 text-red-700 hover:bg-red-50"
                          onClick={() => openDecision(app, 'reject')} data-testid={`reject-${app.id}`}>
                          <XCircle size={12} /> Reject
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* v36 — Approve / Reject Decision Dialog with mandatory comment */}
      {decisionDialog && (
        <Dialog open onOpenChange={(o) => { if (!o) closeDecision(); }}>
          <DialogContent className="max-w-md" data-testid="leave-decision-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {decisionDialog.action === 'approve' ? (
                  <><CheckCircle2 size={18} className="text-emerald-600" /> Approve Leave Application</>
                ) : (
                  <><XCircle size={18} className="text-red-600" /> Reject Leave Application</>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                <p className="font-medium text-slate-800">
                  {decisionDialog.app.user ? `${decisionDialog.app.user.first_name || ''} ${decisionDialog.app.user.last_name || ''}`.trim() : 'Staff member'}
                </p>
                <p className="text-slate-500 mt-0.5">
                  {decisionDialog.app.leave_type?.name || decisionDialog.app.leave_type_id || 'Leave'} · {decisionDialog.app.from_date || decisionDialog.app.start_date} → {decisionDialog.app.to_date || decisionDialog.app.end_date} · {decisionDialog.app.days || 1} day(s)
                </p>
                {decisionDialog.app.reason && (
                  <p className="text-slate-600 mt-2"><span className="font-medium">Reason:</span> {decisionDialog.app.reason}</p>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">
                  {decisionDialog.action === 'approve' ? 'Approval Comment' : 'Rejection Reason'} <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={cn(
                    'w-full h-24 px-3 py-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 border-slate-200',
                    decisionDialog.action === 'approve' ? 'focus:ring-emerald-500' : 'focus:ring-red-500'
                  )}
                  placeholder={decisionDialog.action === 'approve'
                    ? 'e.g., Approved. Please ensure handover to your backup.'
                    : 'e.g., Rejected — peak season; reapply after Aug 15.'}
                  value={decisionComment}
                  onChange={e => setDecisionComment(e.target.value)}
                  autoFocus
                  data-testid="leave-decision-comment"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Required. Min 5 characters. The applicant will see this comment.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={closeDecision} disabled={decisionSubmitting} data-testid="leave-decision-cancel">Cancel</Button>
              <Button
                onClick={submitDecision}
                disabled={decisionSubmitting || decisionComment.trim().length < 5}
                className={cn('gap-2',
                  decisionDialog.action === 'approve'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-red-600 hover:bg-red-700'
                )}
                data-testid="leave-decision-submit">
                {decisionSubmitting
                  ? 'Saving…'
                  : decisionDialog.action === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Apply Leave Dialog */}
      {showApply && (
        <ApplyLeaveDialog
          leaveTypes={leaveTypes}
          onClose={() => setShowApply(false)}
          onSuccess={() => { setShowApply(false); loadData(); }}
        />
      )}
    </div>
  );
};

const ApplyLeaveDialog = ({ leaveTypes, onClose, onSuccess }) => {
  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!leaveType || !startDate || !endDate || !reason) {
      toast.error('Please fill all fields');
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);

    setSubmitting(true);
    try {
      await leaveAPI.apply({
        leave_type_id: leaveType,
        from_date: startDate,
        to_date: endDate,
        days: diffDays,
        reason,
      });
      toast.success('Leave application submitted');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarDays size={18} className="text-teal-600" /> Apply for Leave</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Leave Type</label>
            <Select value={leaveType} onValueChange={setLeaveType}>
              <SelectTrigger className="h-10" data-testid="leave-type-select">
                <SelectValue placeholder="Select leave type" />
              </SelectTrigger>
              <SelectContent>
                {leaveTypes.length > 0 ? leaveTypes.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name} ({t.days_allowed} days/year)</SelectItem>
                )) : (
                  <>
                    <SelectItem value="casual">Casual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="earned">Earned Leave</SelectItem>
                    <SelectItem value="maternity">Maternity Leave</SelectItem>
                    <SelectItem value="paternity">Paternity Leave</SelectItem>
                    <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">From Date</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-10" data-testid="leave-start-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">To Date</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-10" data-testid="leave-end-date" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Reason</label>
            <textarea
              className="w-full h-20 px-3 py-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 border-slate-200"
              placeholder="Enter reason for leave..."
              value={reason}
              onChange={e => setReason(e.target.value)}
              data-testid="leave-reason"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}
            className="bg-teal-600 hover:bg-teal-700 gap-2" data-testid="submit-leave-btn">
            {submitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeavePage;
